import { FollowButtonOutline, FollowButtonVariant, theme, useResponsive } from "@product/scmp-sdk";
import isEmpty from "lodash/isEmpty";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { graphql, useFragment } from "react-relay";

import { EntityFollowButton } from "~/components/entity-follow-button";
import { ZIndexLayer } from "~/lib/styles";
import type { topicHeaderTopic$key } from "~/queries/__generated__/topicHeaderTopic.graphql";

import {
  Background,
  Container,
  Content,
  Description,
  DrawerCardContent,
  ImageIcon,
  ImageIconContainer,
  Info,
  Label,
  RelatedLinksContainer,
  RelatedLinksDivider,
  StyledDrawer,
  StyledRelatedLinks,
  StyledSponsorTag,
  Title,
  TitleContainer,
} from "./styles";

type Props = {
  className?: string;
  reference: topicHeaderTopic$key;
};

export const TopicHeader: FunctionComponent<Props> = ({ className, reference: reference_ }) => {
  const topic = useFragment(
    graphql`
      fragment topicHeaderTopic on Topic {
        ...topicHeaderRelatedLinksTopic
        ...entityFollowButtonBase
        description {
          html
        }
        mobileImage: image(type: MOBILE_APP_IMAGE) {
          alt
          title
          style(filter: { style: "118x118" }) {
            url
          }
        }
        breakoutImage: image(type: BREAKOUT) {
          alt
          title
          style(filter: { style: "2000x792" }) {
            url
          }
        }
        breakoutSponsorImage: image(type: BREAKOUT_SPONSOR) {
          alt
          title
          style(filter: { style: "1560x218" }) {
            url
          }
        }
        name
        sponsor {
          ...sponsorTagSponsor
        }
      }
    `,
    reference_,
  );

  const isMobileView = useResponsive(theme.breakpoints.only("mobile"), false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawerCloseClick = () => setIsDrawerOpen(false);
  const handleDrawerOpenClick = () => setIsDrawerOpen(true);

  const backgroundImage = topic.breakoutSponsorImage?.style?.url
    ? {
        ...topic.breakoutSponsorImage,
        aspectRatio: "1560 / 218",
      }
    : {
        ...topic.breakoutImage,
        aspectRatio: "2000 / 792",
      };
  const backgroundImageUrl = backgroundImage?.style?.url;
  const iconImage = topic.mobileImage;

  const followButtonVariant =
    !isMobileView && !isEmpty(backgroundImageUrl)
      ? FollowButtonVariant.Dark
      : FollowButtonVariant.Light;

  const sponsorColorMap: Record<
    Exclude<FollowButtonVariant, "magazines-style" | "homepage">,
    string
  > = {
    dark: "#fff",
    light: "#001246",
  };

  return (
    <Container className={className}>
      {backgroundImageUrl && (
        <Background
          $aspectRatio={backgroundImage.aspectRatio}
          $objectFit="cover"
          alt={backgroundImage.alt ?? backgroundImage.title ?? ""}
          lazyload={false}
          src={backgroundImageUrl}
        />
      )}

      <Content $hasBackgroundImage={!!backgroundImageUrl}>
        <Label>Topic</Label>

        <TitleContainer>
          <Title>
            {topic.name}
            {topic.description?.html && <Info onClick={handleDrawerOpenClick}>i</Info>}
          </Title>

          {iconImage?.style?.url && (
            <ImageIconContainer>
              <ImageIcon
                $aspectRatio="1"
                alt={iconImage.alt ?? iconImage.title ?? ""}
                lazyload={false}
                src={iconImage.style.url}
              />
            </ImageIconContainer>
          )}
        </TitleContainer>
        <EntityFollowButton
          outline={FollowButtonOutline.Outlined}
          reference={topic}
          variant={followButtonVariant}
        />
        {topic.description?.html && (
          <Description dangerouslySetInnerHTML={{ __html: topic.description?.html }} />
        )}

        {topic.sponsor && (
          <StyledSponsorTag
            color={sponsorColorMap[followButtonVariant]}
            gap="5px"
            imageVariant="large"
            layoutDirection="vertical"
            nameFontSize="14px"
            reference={topic.sponsor}
            typeFontSize="12px"
          />
        )}
      </Content>

      <RelatedLinksContainer>
        <StyledRelatedLinks reference={topic} />
      </RelatedLinksContainer>
      <RelatedLinksDivider />

      <StyledDrawer
        anchor="bottom"
        open={isDrawerOpen && isMobileView}
        PaperProps={{
          sx: {
            backgroundColor: "rgba(244, 244, 240, 0.9)",
          },
        }}
        sx={{
          zIndex: `${ZIndexLayer.Drawer}`,
        }}
        title={topic.name}
        withClose
        onClose={handleDrawerCloseClick}
      >
        {topic.description?.html && (
          <DrawerCardContent dangerouslySetInnerHTML={{ __html: topic.description?.html }} />
        )}
      </StyledDrawer>
    </Container>
  );
};

TopicHeader.displayName = "TopicHeader";
